// src/pages/intranet/layouts/MainLayout.jsx
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Landmark, FileText, Wallet, LogOut, Compass, Bell, Shield, Video, Newspaper, BookOpen, Users, Calendar } from 'lucide-react';

export default function MainLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/intranet/login');
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'APRENDIZ': return 'bg-white/10 text-gray-300 border border-white/20';
      case 'COMPAÑERO': return 'bg-blue-900/30 text-blue-300 border border-blue-800/50';
      case 'MAESTRO': return 'bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/50';
      case 'ADMIN': return 'bg-red-900/30 text-red-400 border border-red-800/50';
      default: return 'bg-gray-800 text-gray-400 border border-gray-700';
    }
  };

  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'H';
  };

  const isActive = (path) => location.pathname === path;

  const getHeaderTitle = () => {
    if (isActive('/intranet/inicio')) return "Panel de Control";
    if (isActive('/intranet/datos-logia')) return "Archivo Institucional";
    if (isActive('/intranet/asistencia')) return "Registro de Asistencias"; // NUEVO
    if (isActive('/intranet/secretaria')) return "Despacho de Secretaría";
    if (isActive('/intranet/tesoreria')) return "Gestión Financiera";
    if (isActive('/intranet/admin')) return "Panel de Gran Oriente";
    if (isActive('/intranet/admin/videos')) return "Gestión de Videos";
    if (isActive('/intranet/admin/noticias')) return "Gestión de Noticias";
    if (isActive('/intranet/admin/publicaciones')) return "Gestión de Publicaciones";
    return "Intranet";
  };

  return (
    <div className="flex h-screen w-full bg-[#050505] font-sans selection:bg-[#C5A059] selection:text-[#050505] overflow-hidden text-white">
      
      {/* SIDEBAR */}
      <aside className="w-64 flex flex-col bg-[#0a0a0a] border-r border-white/5 z-20 relative shadow-[5px_0_15px_rgba(0,0,0,0.5)]">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
        
        <div className="p-6 flex flex-col items-center justify-center border-b border-white/10 relative z-10 text-center">
          <Compass size={32} className="text-[#C5A059] mb-3" />
          <h2 className="font-serif text-xl tracking-wider text-white m-0">GLP Intranet</h2>
          <span className="text-[#C5A059] text-[9px] tracking-[0.3em] uppercase mt-2">Sistema Oficial</span>
        </div>

        <nav className="flex-1 p-4 flex flex-col gap-2 relative z-10 overflow-y-auto hide-scrollbar">
          <p className="text-gray-500 text-[10px] tracking-[0.2em] uppercase font-semibold mb-2 ml-2 mt-4">Menú Principal</p>
          
          <Link to="/intranet/inicio" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/inicio') ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
            <Home size={18} /> <span>Inicio</span>
          </Link>
          <Link to="/intranet/datos-logia" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/datos-logia') ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
            <Landmark size={18} /> <span>Datos Logia</span>
          </Link>
          {/* NUEVA RUTA DE ASISTENCIA */}
          <Link to="/intranet/asistencia" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/asistencia') ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
            <Calendar size={18} /> <span>Asistencias</span>
          </Link>
          <Link to="/intranet/secretaria" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/secretaria') ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
            <FileText size={18} /> <span>Secretaría</span>
          </Link>
          <Link to="/intranet/tesoreria" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/tesoreria') ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
            <Wallet size={18} /> <span>Tesorería</span>
          </Link>

          {/* MENÚ ADMINISTRADOR (Solo visible si es ADMIN) */}
          {user?.role === 'ADMIN' && (
            <>
              <p className="text-red-500/70 text-[10px] tracking-[0.2em] uppercase font-semibold mb-2 ml-2 mt-6 border-t border-white/10 pt-4">Administración GO</p>
              
              <Link to="/intranet/admin" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/admin') ? 'bg-gradient-to-r from-red-900/40 to-transparent text-red-400 border-l-2 border-red-500' : 'text-gray-400 hover:text-red-300 hover:bg-white/5 border-l-2 border-transparent'}`}>
                <Shield size={18} /> <span>Dashboard Global</span>
              </Link>
              <Link to="/intranet/admin/noticias" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/admin/noticias') ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
                <Newspaper size={18} /> <span>Noticias</span>
              </Link>
              <Link to="/intranet/admin/publicaciones" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/admin/publicaciones') ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
                <BookOpen size={18} /> <span>Publicaciones</span>
              </Link>
              <Link to="/intranet/admin/videos" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/admin/videos') ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
                <Video size={18} /> <span>Videos</span>
              </Link>
              <Link to="/intranet/admin/solicitudes" className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-light transition-all duration-300 ${isActive('/intranet/admin/solicitudes') ? 'bg-gradient-to-r from-[#C5A059]/20 to-transparent text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
                <Users size={18} /> <span>Solicitudes</span>
              </Link>
            </>
          )}
        </nav>

        <div className="p-5 border-t border-white/10 text-center relative z-10 bg-[#050505]">
          <p className="text-gray-500 text-xs tracking-widest uppercase m-0">© 2026 G.L.P.</p>
          <p className="text-gray-600 text-[10px] mt-1 m-0">v1.0.0 Oficial</p>
        </div>
      </aside>

      {/* CONTENEDOR DERECHO */}
      <main className="flex-1 flex flex-col overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-5 mix-blend-overlay pointer-events-none z-0"></div>
        
        <header className="h-[80px] bg-[#0a0a0a]/95 backdrop-blur-md flex justify-between items-center px-8 border-b border-white/5 z-10 shadow-lg relative">
          <div className="flex items-center">
            <h3 className="m-0 font-serif text-xl md:text-2xl text-white tracking-wide">
              {getHeaderTitle()}
            </h3>
          </div>

          <div className="flex items-center gap-6">
            <button className="text-gray-400 hover:text-[#C5A059] transition-colors bg-transparent border-none cursor-pointer flex items-center justify-center p-2 rounded-full hover:bg-white/5">
              <Bell size={20} />
            </button>

            <div className="h-8 w-[1px] bg-white/10"></div>

            <div className="flex items-center gap-4">
              <div className="flex flex-col items-end justify-center">
                <span className="m-0 text-sm font-semibold text-white tracking-wide">
                  {user?.username || 'Usuario'}
                </span>
                <span className={`mt-1 px-2 py-[2px] rounded-sm text-[9px] uppercase tracking-widest font-mono ${getRoleBadgeClass(user?.role)}`}>
                  {user?.role || 'Invitado'}
                </span>
              </div>
              <div className="w-10 h-10 rounded-sm bg-[#050505] border border-[#C5A059]/40 text-[#C5A059] flex justify-center items-center font-serif text-lg shadow-[0_0_10px_rgba(197,160,89,0.1)]">
                {getInitial(user?.username)}
              </div>
            </div>

            <button onClick={handleLogout} className="ml-2 bg-transparent hover:bg-red-900/20 border border-red-900/30 text-red-400 cursor-pointer flex items-center justify-center p-2 rounded-md transition-all duration-300" title="Cerrar Sesión">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <div className="flex-1 p-6 md:p-10 overflow-y-auto relative z-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}